import { useState } from 'react';
import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SettingIcon } from '@Assets/setting_icon.svg';
import {
  PageWrapper,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@Components';
import { ConfigCPFR } from '@Models';
import UnconfiguredPage from './components/UnconfiguredPage';
import DemandSection from './sections/DemandSection';
import InventorySection from './sections/InventorySection';
import SupplySection from './sections/SupplySection';
import TargetInventorySection from './sections/TargetInventorySection';

interface ConfigurationPolicyCPFRProps {
  config: ConfigCPFR;
  readOnly?: boolean;
}

const ConfigurationPolicyCPFR = ({ config, readOnly = true }: ConfigurationPolicyCPFRProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('targetInventory');

  const handleChange = () => {};

  // Show unconfigured page
  if (config.State === 'Unconfigured' || !config.Config) {
    return <UnconfiguredPage connector={config.Connector} />;
  }

  const tabs = [
    { id: 'targetInventory', label: t('ConfigurationPolicy.CPFR.TargetInventory') },
    { id: 'demand', label: t('ConfigurationPolicy.CPFR.Demand') },
    { id: 'supply', label: t('ConfigurationPolicy.CPFR.Supply') },
    { id: 'inventory', label: t('ConfigurationPolicy.CPFR.InventoryAvailable') },
  ];

  return (
    <div className="h-full w-full">
      <PageWrapper
        title={t('ConfigurationPolicy.CPFR.Title')}
        description={
          <span className="flex items-center gap-2 text-[17px] text-gray-600">
            <SettingIcon stroke="#667085" fill="none" strokeWidth={1.5} className="w-[19px] h-[19px]" />
            {config.Connector}
          </span>
        }
        actions={
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-gray-600 cursor-help">
                  <Lock size={18} />
                </span>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{t('ConfigurationPolicy.LockIconTooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        }>
        <div className="flex flex-col gap-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-4 h-auto cursor-pointer">
              {tabs.map((tab, index) => {
                const isActive = tab.id === activeTab;
                const isFirst = index === 0;
                const isLast = index === tabs.length - 1;
                const borderClass = isActive ? (isFirst ? 'border-r' : isLast ? 'border-l' : 'border-l border-r') : '';

                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className={`py-3 bg-gray-50 border-0 text-gray-500 text-[15px] border-gray-200 rounded-md
                    data-[state=active]:bg-white
                    ${borderClass}
                    data-[state=active]:border-solid
                    data-[state=active]:h-auto
                    data-[state=active]:shadow-sm
                    data-[state=active]:border-gray-200
                    data-[state=active]:text-gray-700 cursor-pointer`}>
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <div className="mt-4">
              <TabsContent value="targetInventory" className="animate-slide-in">
                <TargetInventorySection
                  value={config.Config.TargetInventory}
                  onChange={handleChange}
                  readOnly
                  connector={config.Connector}
                />
              </TabsContent>

              <TabsContent value="inventory" className="animate-slide-in">
                <InventorySection value={config.Config.InventoryAvailable} onChange={handleChange} />
              </TabsContent>

              <TabsContent value="demand" className="animate-slide-in">
                <DemandSection value={config.Config.Demand} onChange={handleChange} readOnly={readOnly} />
              </TabsContent>

              <TabsContent value="supply" className="animate-slide-in">
                <SupplySection value={config.Config.Supply} onChange={handleChange} readOnly={readOnly} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </PageWrapper>
    </div>
  );
};

export default ConfigurationPolicyCPFR;
